import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Save,
  Download,
  Clock,
  Send,
  Eye,
  Loader2,
  ArrowLeft,
  FileText,
  Check,
  Maximize2,
  Minimize2,
  ExternalLink,
  Handshake,
  Video,
  Contrast,
  Link2,
  ShieldCheck,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import type { TiptapQuoteEditorHandle } from './TiptapQuoteEditor';
import QuoteWorkspace from './QuoteWorkspace';
import EditorViewTabs, { type EditorViewMode } from './EditorViewTabs';
import './quoteWorkspace.css';
import IntroVideoSettingsDialog from './IntroVideoSettingsDialog';
import GlobalToolbar from './GlobalToolbar';
import PageListSidebar, { getBlockTitle } from './PageListSidebar';
import { AIChatPanel } from './AIChatPanel';
import PublishDialog from './dialogs/PublishDialog';
import WithdrawDialog from './dialogs/WithdrawDialog';
import DeletePageDialog from './dialogs/DeletePageDialog';
import SaveAsTemplateDialog from './dialogs/SaveAsTemplateDialog';
import SavePageToLibraryDialog from '@/components/library/SavePageToLibraryDialog';
import QuoteReviewDialog from './dialogs/QuoteReviewDialog';
import { EditorProvider } from '@/store/editorContext';
import { useNavigate } from 'react-router-dom';
import type { Block, Quote, TemplateKind } from '@/types';
import { useBlockStore } from '@/store/blockStore';
import { usePageLibraryStore } from '@/store/pageLibraryStore';
import { useQuoteStore } from '@/store/quoteStore';
import api from '@/lib/api';
import { openStandaloneApp } from '@/lib/hubspotSession';
import { closeHubSpotModal, hubspotRecordUrl, useHubSpotContext } from '@/lib/hubspotContext';
import { cn } from '@/lib/utils';
import { usePageThumbnails } from '@/hooks/usePageThumbnails';
import { useHubSpotQuoteData } from '@/hooks/useHubSpotQuoteData';
import { useQuoteVariables } from '@/hooks/useQuoteVariables';
import { useQuoteSaveState } from '@/hooks/useQuoteSaveState';
import { useQuotePublishing, UBO_SIGNERS } from '@/hooks/useQuotePublishing';
import { useAIBlockHandlers } from '@/hooks/useAIBlockHandlers';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuoteReview } from '@/hooks/useQuoteReview';
import { syncQuoteProductUsages } from '@/hooks/useQuoteProductStatus';

interface PDFEditorProps {
  quote: Quote;
  /** Soort template van een werkkopie: een bibliotheekpagina heet in de editor anders. */
  templateKind?: TemplateKind;
}

/** Een template heeft geen deal: variabelen blijven zichtbaar als {{deal.name}}. */
const TEMPLATE_VARIABLES: Record<string, string> = {};
const keepVariables = (text: string) => text;

export default function PDFEditor({ quote: initialQuote, templateKind = 'quote' }: PDFEditorProps) {
  const navigate = useNavigate();
  const context = useHubSpotContext();
  /** Werkkopie van een template: geen deal, geen klantlink, opslaan schrijft naar de template. */
  const templateId = initialQuote.templateId || null;
  const isLibraryPage = templateKind === 'page';
  const draftOverviewPath = isLibraryPage ? '/?tab=pages' : '/?tab=templates';
  const saveToTemplateLabel = isLibraryPage ? 'Opslaan in bibliotheek' : 'Opslaan in template';
  const portalId = initialQuote.portalId || context.portalId;
  const dealId = templateId ? undefined : initialQuote.dealId || context.dealId;

  useEffect(() => {
    if (initialQuote.portalId) localStorage.setItem('hubspot_portal_id', initialQuote.portalId);
    if (initialQuote.dealId) localStorage.setItem('hubspot_deal_id', initialQuote.dealId);
  }, [initialQuote.portalId, initialQuote.dealId]);
  const {
    blocks,
    setBlocks,
    updateBlock,
    deleteBlock,
    pendingBlockSaves,
    failedBlockSaves,
  } = useBlockStore();
  const { updateQuote, setCurrentQuote } = useQuoteStore();

  const [localQuote, setLocalQuote] = useState(initialQuote);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [openingStandaloneApp, setOpeningStandaloneApp] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editorView, setEditorView] = useState<EditorViewMode>(() =>
    localStorage.getItem('ubo-quote-view') === 'document' ? 'document' : 'side-by-side'
  );
  const handleViewChange = useCallback((next: EditorViewMode) => {
    setEditorView(next);
    localStorage.setItem('ubo-quote-view', next);
  }, []);
  const [introVideoDialogOpen, setIntroVideoDialogOpen] = useState(false);
  const isInIframe = typeof window !== 'undefined' && window !== window.parent;

  const tiptapEditorRef = useRef<TiptapQuoteEditorHandle | null>(null);

  // Pagina toevoegen toont alleen de bibliotheek: alvast ophalen, dan is het menu meteen gevuld.
  useEffect(() => {
    usePageLibraryStore.getState().ensurePages();
  }, []);

  const {
    hubspotData,
    hubspotDataLoaded,
    hubspotDataErrors,
    hubspotDataRefreshing,
    refreshHubSpotData,
    primaryContact,
  } = useHubSpotQuoteData(portalId, dealId);
  const dealUrl = portalId && dealId ? hubspotRecordUrl(portalId, '0-3', dealId) : null;

  useEffect(() => {
    if (localQuote.title === 'Nieuwe Offerte') {
      setLocalQuote((prev) => ({ ...prev, title: '{{deal.name}}' }));
    }
  }, [localQuote.title]);

  useEffect(() => {
    const dealName = hubspotData.deal?.properties?.dealname;
    if (
      dealName &&
      (localQuote.title === '{{deal.name}}' || localQuote.title === 'Nieuwe Offerte')
    ) {
      setLocalQuote((prev) => ({ ...prev, title: dealName }));
    }
  }, [hubspotData.deal?.properties?.dealname, localQuote.title]);

  const quoteVariables = useQuoteVariables({
    hubspotData,
    primaryContact,
    localQuote,
    blocks,
    hubspotDataLoaded,
  });
  const variablesMap = templateId ? TEMPLATE_VARIABLES : quoteVariables.variablesMap;
  const resolveVariables = templateId ? keepVariables : quoteVariables.resolveVariables;

  const { isSaving, saveState, saveLabel, handleSave, flushEditorAndPendingUpdates } =
    useQuoteSaveState({
      localQuote,
      updateQuote,
      tiptapEditorRef,
      pendingBlockSaves,
      failedBlockSaves,
    });

  const handleIntroVideoSave = async (url: string | null) => {
    setLocalQuote((prev) => {
      // Alleen het gewijzigde veld; een volledige kopie zou een verouderde status terugschrijven.
      void updateQuote(prev.id, { introVideoUrl: url });
      return { ...prev, introVideoUrl: url };
    });
  };

  const clientLogoWhiteFilterOnDark =
    localQuote.layoutConfig?.clientLogoWhiteFilterOnDark !== false;

  const handleClientLogoWhiteFilterChange = useCallback(
    (next: boolean) => {
      setLocalQuote((prev) => {
        const base = {
          ...(typeof prev.metadata === 'object' && prev.metadata ? { ...prev.metadata } : {}),
          ...(prev.layoutConfig && typeof prev.layoutConfig === 'object'
            ? { ...prev.layoutConfig }
            : {}),
        };
        const layoutConfig = { ...base, clientLogoWhiteFilterOnDark: next };
        void updateQuote(prev.id, { layoutConfig });
        return { ...prev, layoutConfig, metadata: layoutConfig };
      });
    },
    [updateQuote]
  );

  const handleGeneratePDF = async () => {
    setPdfLoading(true);
    try {
      const saved = await flushEditorAndPendingUpdates();
      if (!saved) {
        toast.error(
          'PDF niet gemaakt: de laatste wijzigingen konden niet worden opgeslagen',
          { error: useBlockStore.getState().lastBlockSaveError }
        );
        return;
      }
      await api.downloadPDF(localQuote.id, `${localQuote.title || 'offerte'}.pdf`);
      toast.success('PDF gedownload');
    } catch (error) {
      toast.error('PDF genereren mislukt', { error });
    } finally {
      setPdfLoading(false);
    }
  };

  /** Het overzicht leest de opgeslagen offerte; eerst opslaan, anders mist de laatste wijziging. */
  const handleOpenHours = async () => {
    const saved = await flushEditorAndPendingUpdates();
    if (!saved) {
      toast.error(
        'Urenoverzicht niet geopend: de laatste wijzigingen konden niet worden opgeslagen',
        { error: useBlockStore.getState().lastBlockSaveError }
      );
      return;
    }
    navigate(`/quotes/${localQuote.id}/hours`);
  };

  /** De kaart toont de mailknop alleen bij een verstuurde of bekeken offerte. */
  const canMailViaHubSpot =
    isInIframe && ['SENT', 'VIEWED'].includes(String(localQuote.status).toUpperCase());

  /** Sluit de editor zodat de HubSpot-kaart het e-mailvenster kan openen; eerst opslaan. */
  const handleSendViaHubSpot = async () => {
    const saved = await flushEditorAndPendingUpdates();
    if (!saved) {
      toast.error(
        'Editor niet gesloten: de laatste wijzigingen konden niet worden opgeslagen',
        { error: useBlockStore.getState().lastBlockSaveError }
      );
      return;
    }
    closeHubSpotModal();
  };

  const handleBlocksChange = useCallback(
    (newBlocks: Block[]) => {
      const currentBlocks = useBlockStore.getState().blocks;
      if (newBlocks.length === 0 && currentBlocks.length > 0) {
        console.warn('[handleBlocksChange] Geblokkeerd: sync zou alle pagina’s verwijderen');
        return;
      }
      const prevById = new Map(currentBlocks.map((b) => [b.id, b]));
      const merged = newBlocks.map((block) => {
        const prev = prevById.get(block.id);
        return prev ? { ...block, createdAt: prev.createdAt } : block;
      });
      setBlocks(merged, { preserveStructuralHistory: true });
      merged.forEach((block) => {
        updateBlock(block.id, {
          content: block.content,
          styles: block.styles,
          config: block.config,
          quoteId: block.quoteId,
          type: block.type,
          order: block.order,
        });
      });
    },
    [setBlocks, updateBlock]
  );

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  const handleSidebarSelect = useCallback((block: Block) => {
    tiptapEditorRef.current?.selectBlock(block.id);
    setSelectedBlockId(block.id);
    const el = document.querySelector(`[data-block-id="${block.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  }, []);

  const handleSidebarReorder = useCallback(
    async (blockIds: string[]) => {
      try {
        if (!tiptapEditorRef.current?.reorderPages(blockIds)) return;
        tiptapEditorRef.current.flushSync();
        await api.reorderBlocks(localQuote.id, blockIds);
        toast.success('Volgorde bijgewerkt');
      } catch (error) {
        toast.error('Volgorde opslaan mislukt', { error });
      }
    },
    [localQuote.id]
  );

  const [deletePageTarget, setDeletePageTarget] = useState<Block | null>(null);
  const [deletePageLoading, setDeletePageLoading] = useState(false);

  const handleSidebarDelete = useCallback(
    (block: Block) => {
      if (blocks.length <= 1) {
        toast.error('Je hebt minimaal 1 pagina nodig');
        return;
      }
      setDeletePageTarget(block);
    },
    [blocks.length]
  );

  const handleConfirmDeletePage = useCallback(async () => {
    if (!deletePageTarget) return;
    setDeletePageLoading(true);
    try {
      tiptapEditorRef.current?.cancelPendingSync();
      await deleteBlock(deletePageTarget.id);
      if (selectedBlockId === deletePageTarget.id) setSelectedBlockId(null);
      setDeletePageTarget(null);
      toast.success('Pagina verwijderd');
    } catch (error) {
      toast.error('Pagina verwijderen mislukt', { error });
    } finally {
      setDeletePageLoading(false);
    }
  }, [deletePageTarget, deleteBlock, selectedBlockId]);

  const thumbnails = usePageThumbnails(blocks.map((b) => b.id));

  const {
    shareUrl,
    publicPreviewUrl,
    publishOpen,
    setPublishOpen,
    publishStep,
    setPublishStep,
    publishLoading,
    withdrawOpen,
    setWithdrawOpen,
    withdrawLoading,
    isPublished,
    expiryDate,
    setExpiryDate,
    publishLocale,
    setPublishLocale,
    signEmail,
    setSignEmail,
    signName,
    setSignName,
    signStatus,
    selectedContactIds,
    selectedUboSigner,
    setSelectedUboSigner,
    canSignOnPublish,
    signOnPublish,
    setSignOnPublish,
    senderSignature,
    setSenderSignature,
    senderSignedName,
    contactOptions,
    handleContactToggle,
    handleCopyLink,
    handleCopyPreviewLink,
    hasSignaturePage,
    canWithdraw,
    handleOpenPublish,
    handleConfirmPublish,
    handleWithdrawQuote,
  } = useQuotePublishing({
    localQuote,
    setLocalQuote,
    initialQuote,
    blocks,
    dealId,
    contacts: hubspotData.contacts,
    primaryContact,
    updateQuote,
    setCurrentQuote,
    updateBlock,
    flushEditorAndPendingUpdates,
  });

  const [reviewOpen, setReviewOpen] = useState(false);
  const review = useQuoteReview(localQuote.id);

  const prepareReviewScope = useCallback(async () => {
    const saved = await flushEditorAndPendingUpdates();
    if (!saved) return false;
    try {
      await syncQuoteProductUsages(localQuote.id, useBlockStore.getState().blocks);
      return true;
    } catch (error) {
      toast.error('Productregels konden niet worden voorbereid voor review', { error });
      return false;
    }
  }, [flushEditorAndPendingUpdates, localQuote.id]);

  const handleRequestReview = useCallback(async () => {
    const next = await review.requestReview(prepareReviewScope);
    if (!next) return;
    if (next.delivery?.failed) {
      toast.warning(
        `${next.delivery.sent} uitnodigingen verstuurd, ${next.delivery.failed} niet verzonden. Controleer de e-mailconfiguratie.`
      );
    } else {
      toast.success(
        `${next.reviewers.length} reviewuitnodiging${next.reviewers.length === 1 ? '' : 'en'} verstuurd`
      );
    }
  }, [prepareReviewScope, review]);

  const handlePublishClick = useCallback(async () => {
    if (isPublished) {
      handleOpenPublish();
      return;
    }
    const prepared = await prepareReviewScope();
    if (!prepared) return;
    const state = await review.refresh();
    if (state?.canPublish) handleOpenPublish();
    else setReviewOpen(true);
  }, [handleOpenPublish, isPublished, prepareReviewScope, review]);

  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  const { handleAICreateBlock, handleAIDeleteBlock, handleAIMoveBlock } = useAIBlockHandlers({
    blocks,
    quoteId: localQuote.id,
    setBlocks,
    updateBlock,
    tiptapEditorRef,
  });

  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saveAsTemplateName, setSaveAsTemplateName] = useState('');
  const [saveAsTemplateDesc, setSaveAsTemplateDesc] = useState('');
  const [saveAsTemplateLoading, setSaveAsTemplateLoading] = useState(false);

  const handleSaveAsTemplate = async () => {
    if (!saveAsTemplateName.trim()) return;
    setSaveAsTemplateLoading(true);
    try {
      await api.createTemplateFromQuote({
        quoteId: localQuote.id,
        name: saveAsTemplateName.trim(),
        description: saveAsTemplateDesc.trim() || undefined,
      });
      toast.success('Template opgeslagen!');
      setSaveAsTemplateOpen(false);
      setSaveAsTemplateName('');
      setSaveAsTemplateDesc('');
    } catch (err) {
      console.error('Save as template failed:', err);
      toast.error('Template opslaan mislukt', { error: err });
    } finally {
      setSaveAsTemplateLoading(false);
    }
  };

  /** Pagina die je in de paginabibliotheek bewaart; de dialoog staat open zolang hij gezet is. */
  const [libraryPage, setLibraryPage] = useState<{ blockId: string; defaultName: string } | null>(null);

  const [templateAction, setTemplateAction] = useState<'save' | 'discard' | null>(null);
  const [discardTemplateOpen, setDiscardTemplateOpen] = useState(false);

  /** Eerst alles in de werkkopie, dan de werkkopie naar de template. */
  const handleSaveToTemplate = async () => {
    if (!templateId || templateAction) return;
    setTemplateAction('save');
    // Wat je tijdens het opslaan nog typt, zou na de flush verloren gaan.
    (document.activeElement as HTMLElement | null)?.blur();
    try {
      const flushed = await flushEditorAndPendingUpdates();
      if (!flushed) {
        toast.error(
          'Template niet opgeslagen: de laatste wijzigingen konden niet worden bewaard',
          { error: useBlockStore.getState().lastBlockSaveError }
        );
        return;
      }
      tiptapEditorRef.current?.cancelPendingSync();
      const template = await api.saveTemplateDraft(templateId, localQuote.id);
      toast.success(`${isLibraryPage ? 'Bibliotheekpagina' : 'Template'} ‘${template.name}’ opgeslagen`);
      if (isLibraryPage) void usePageLibraryStore.getState().fetchPages();
      navigate(draftOverviewPath);
    } catch (error) {
      toast.error('Template opslaan mislukt', { error });
    } finally {
      setTemplateAction(null);
    }
  };

  const handleDiscardTemplateChanges = async () => {
    if (!templateId || templateAction) return;
    setTemplateAction('discard');
    (document.activeElement as HTMLElement | null)?.blur();
    try {
      // Openstaande saves eerst afronden, anders schrijven ze naar een verwijderde werkkopie.
      await flushEditorAndPendingUpdates();
      tiptapEditorRef.current?.cancelPendingSync();
      await api.discardTemplateDraft(templateId);
      setDiscardTemplateOpen(false);
      toast.success('Wijzigingen verworpen');
      navigate(draftOverviewPath);
    } catch (error) {
      toast.error('Verwerpen mislukt', { error });
    } finally {
      setTemplateAction(null);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        void handleSaveRef.current();
      }
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  return (
    <EditorProvider boxSignStatus={signStatus}>
      <IntroVideoSettingsDialog
        open={introVideoDialogOpen}
        onOpenChange={setIntroVideoDialogOpen}
        introVideoUrl={localQuote.introVideoUrl}
        onSave={handleIntroVideoSave}
      />
      <div className="quote-editor-shell flex h-dvh min-h-0 flex-col bg-background">
        {/* Compact Top Bar */}
        {!isFullscreen && (
          <header className="shrink-0 flex items-center quote-editor-header h-[72px] bg-[#1a1a2e] border-b border-white/10 px-4 gap-3">
            <button
              onClick={() => navigate(templateId ? draftOverviewPath : '/quotes')}
              title={
                templateId
                  ? isLibraryPage
                    ? 'Terug naar de paginabibliotheek'
                    : 'Terug naar templates'
                  : 'Terug naar offerte-overzicht'
              }
              className="flex items-center justify-center h-8 w-8 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <div className="h-5 w-px bg-white/10" />

            {templateId && (
              <span
                className="shrink-0 rounded border border-white/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/50"
                title={
                  isLibraryPage
                    ? 'Je bewerkt een bibliotheekpagina. Wie hem invoegt krijgt deze inhoud pas na ‘Opslaan in bibliotheek’; offertes waar hij al in staat veranderen niet mee.'
                    : 'Je bewerkt een template. Nieuwe offertes krijgen deze inhoud pas na ‘Opslaan in template’.'
                }
              >
                {isLibraryPage ? 'Bibliotheek' : 'Template'}
              </span>
            )}

            {localQuote.quoteNumber && (
              <span className="hidden sm:inline text-[11px] font-mono text-white/40 shrink-0">
                {localQuote.quoteNumber}
              </span>
            )}

            <input
              value={resolveVariables(localQuote.title) || localQuote.title}
              onChange={(e) => setLocalQuote({ ...localQuote, title: e.target.value })}
              placeholder={templateId ? (isLibraryPage ? 'Naam van de pagina' : 'Naam van de template') : 'Document titel'}
              className="flex-1 min-w-0 border-none bg-transparent text-sm font-medium text-white placeholder:text-white/40 focus:outline-none"
            />

            <EditorViewTabs value={editorView} onChange={handleViewChange} />

            <div className="hidden sm:flex items-center gap-1.5">
              {Boolean(portalId && dealId) && !hubspotDataLoaded && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 text-white/60 text-[11px] font-medium">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>HubSpot-gegevens laden…</span>
                </div>
              )}
              <div
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium',
                  saveState === 'error'
                    ? 'bg-red-500/20 text-red-300'
                    : saveState === 'dirty'
                      ? 'bg-amber-500/20 text-amber-300'
                      : saveState === 'saving'
                        ? 'bg-white/10 text-white/60'
                        : 'bg-white/10 text-white/50'
                )}
              >
                {saveState === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
                <span
                  className="whitespace-nowrap"
                  title={templateId ? `Je wijzigingen staan veilig in een concept; ‘${saveToTemplateLabel}’ maakt ze definitief.` : undefined}
                >
                  {templateId && saveState === 'saved' ? 'Concept bewaard' : saveLabel}
                </span>
              </div>

              {((initialQuote as Quote & { viewCount?: number }).viewCount ?? 0) > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 text-white/60 text-[11px]">
                  <Eye className="h-3 w-3" />
                  {(initialQuote as Quote & { viewCount?: number }).viewCount}
                </div>
              )}
            </div>

            <div className="hidden sm:block h-5 w-px bg-white/10" />

            <div className="flex items-center gap-1">
              {templateId ? (
                <button
                  type="button"
                  disabled={Boolean(templateAction)}
                  onClick={() => setDiscardTemplateOpen(true)}
                  className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
                  title="Wijzigingen sinds de laatste keer opslaan weggooien"
                >
                  Verwerpen
                </button>
              ) : (
                <>
                  {dealUrl && (
                    <a
                      href={dealUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                      title="Deal openen in HubSpot: contactpersonen, bedrijf en dealgegevens aanpassen"
                      aria-label="Deal openen in HubSpot"
                    >
                      <Handshake className="h-3.5 w-3.5" />
                      <span className="hidden lg:inline">Deal</span>
                    </a>
                  )}

                  <button
                    disabled={pdfLoading}
                    onClick={handleGeneratePDF}
                    className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
                    title="PDF downloaden"
                  >
                    {pdfLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    <span className="hidden lg:inline">PDF</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleOpenHours()}
                    className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    title="Urenoverzicht: de uren achter deze offerte, per uursoort en fase, met CSV-export"
                    aria-label="Urenoverzicht"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    <span className="hidden lg:inline">Uren</span>
                  </button>

                  {publicPreviewUrl ? (
                    <button
                      type="button"
                      onClick={handleCopyPreviewLink}
                      className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                      title="Kopieer preview-link (publieke URL /q/… — bij concept met waarschuwing)"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      <span className="hidden lg:inline">Preview-link</span>
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => setIntroVideoDialogOpen(true)}
                    className={cn(
                      'flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium transition-colors',
                      localQuote.introVideoUrl
                        ? 'bg-violet-500/25 text-violet-200 hover:bg-violet-500/35'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    )}
                    title="Videoboodschap voor de klant (eerste bezoek)"
                  >
                    <Video className="h-3.5 w-3.5" />
                    <span className="hidden lg:inline">Video</span>
                  </button>

                  <button
                    onClick={() => void handlePublishClick()}
                    className={cn(
                      'flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium transition-colors',
                      isPublished
                        ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    )}
                    title={
                      isPublished
                        ? 'Gepubliceerd — klik om te delen of e-mailen'
                        : 'Publiceren & versturen'
                    }
                  >
                    {isPublished ? <Check className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                    <span className="hidden lg:inline">
                      {isPublished ? 'Gepubliceerd' : 'Publiceren'}
                    </span>
                  </button>

                  {review.state?.required && !isPublished && (
                    <button
                      type="button"
                      onClick={() => setReviewOpen(true)}
                      className={cn(
                        'flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium transition-colors lg:px-3',
                        review.state.status === 'approved'
                          ? 'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30'
                          : review.state.status === 'changes_requested'
                            ? 'bg-red-500/20 text-red-200 hover:bg-red-500/30'
                            : 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                      )}
                      title="Productreview bekijken"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      <span className="hidden xl:inline">
                        {review.state.status === 'approved'
                          ? 'Review akkoord'
                          : review.state.status === 'pending'
                            ? 'Review loopt'
                            : 'Review nodig'}
                      </span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSaveAsTemplateName(localQuote.title || 'Template');
                      setSaveAsTemplateDesc('');
                      setSaveAsTemplateOpen(true);
                    }}
                    className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    title="Opslaan als template"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span className="hidden lg:inline">Template</span>
                  </button>
                </>
              )}

              {isInIframe && (
                <button
                  type="button"
                  disabled={openingStandaloneApp}
                  onClick={async () => {
                    setOpeningStandaloneApp(true);
                    try {
                      await openStandaloneApp();
                    } catch (error) {
                      toast.error('Openen van de app mislukt.', { error });
                    } finally {
                      setOpeningStandaloneApp(false);
                    }
                  }}
                  className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
                  title="Open app in nieuw tabblad"
                  aria-label="Open app in nieuw tabblad"
                >
                  {openingStandaloneApp ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ExternalLink className="h-3.5 w-3.5" />
                  )}
                </button>
              )}

              <button
                onClick={() => setIsFullscreen(true)}
                className="flex items-center gap-1.5 h-8 px-2 lg:px-3 rounded-lg text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Volledig scherm"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>

              {templateId ? (
                <button
                  onClick={() => void handleSaveToTemplate()}
                  disabled={Boolean(templateAction)}
                  className="flex items-center gap-1.5 h-8 px-3 lg:px-4 rounded-lg text-[12px] font-semibold bg-[#ff7a59] text-white hover:bg-[#ff5c35] transition-colors disabled:opacity-50"
                  title={
                    isLibraryPage
                      ? 'Wijzigingen opslaan in de bibliotheek; wie de pagina hierna invoegt krijgt deze versie'
                      : 'Wijzigingen opslaan in de template; nieuwe offertes gebruiken dan deze versie'
                  }
                >
                  {templateAction === 'save' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  <span className="hidden lg:inline">{saveToTemplateLabel}</span>
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 h-8 px-3 lg:px-4 rounded-lg text-[12px] font-semibold bg-[#ff7a59] text-white hover:bg-[#ff5c35] transition-colors disabled:opacity-50"
                  title="Opslaan"
                >
                  <Save className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">Opslaan</span>
                </button>
              )}
            </div>
          </header>
        )}

        {/* Floating toolbar in fullscreen mode */}
        {isFullscreen && (
          <div className="absolute top-3 right-3 z-50 flex items-center gap-2 bg-[#1a1a2e]/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-lg">
            <div
              className="flex items-center gap-1.5 pr-1 border-r border-white/15"
              title="Wit klantlogo op donkere pagina’s (links in de header)"
            >
              <Contrast className="h-3.5 w-3.5 text-white/60 shrink-0" aria-hidden />
              <span className="text-[11px] text-white/80 whitespace-nowrap hidden sm:inline">
                Wit klantlogo
              </span>
              <Switch
                checked={clientLogoWhiteFilterOnDark}
                onCheckedChange={handleClientLogoWhiteFilterChange}
                className="scale-90 data-[state=checked]:bg-[#ff7a59]"
              />
            </div>
            <button
              onClick={templateId ? () => void handleSaveToTemplate() : handleSave}
              disabled={templateId ? Boolean(templateAction) : isSaving}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold bg-[#ff7a59] text-white hover:bg-[#ff5c35] transition-colors disabled:opacity-50"
              title={templateId ? saveToTemplateLabel : 'Opslaan'}
            >
              <Save className="h-3.5 w-3.5" />
              <span>{templateId ? saveToTemplateLabel : 'Opslaan'}</span>
            </button>
            <button
              onClick={() => setIsFullscreen(false)}
              className="flex items-center justify-center h-8 w-8 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              title="Volledig scherm verlaten (Esc)"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
          </div>
        )}

        <QuoteWorkspace
          customerContext={{
            data: hubspotData,
            loaded: hubspotDataLoaded,
            errors: hubspotDataErrors,
            portalId,
            dealId,
            isTemplate: Boolean(templateId),
            onRefresh: () => void refreshHubSpotData(),
            refreshing: hubspotDataRefreshing,
          }}
          editorRef={tiptapEditorRef}
          fullscreen={isFullscreen}
          mode={editorView}
          onViewChange={handleViewChange}
          documentToolbar={
            <GlobalToolbar
              onToggleAI={() => setAiPanelOpen((prev) => !prev)}
              aiOpen={aiPanelOpen}
              clientLogoWhiteFilterOnDark={clientLogoWhiteFilterOnDark}
              onClientLogoWhiteFilterOnDarkChange={handleClientLogoWhiteFilterChange}
              onBeforeUndoRedo={() => tiptapEditorRef.current?.cancelPendingSync()}
            />
          }
          sidebar={
            <PageListSidebar
              blocks={blocks}
              selectedBlockId={selectedBlockId}
              onSelectBlock={handleSidebarSelect}
              onReorder={handleSidebarReorder}
              onDeleteBlock={handleSidebarDelete}
              onInsertLibraryPage={(page, afterBlockId) => {
                void tiptapEditorRef.current?.addLibraryPage(page, afterBlockId);
              }}
              onSaveToLibrary={(block) =>
                setLibraryPage({ blockId: block.id, defaultName: getBlockTitle(block, 80) })
              }
              thumbnails={thumbnails}
              layoutConfig={localQuote.layoutConfig}
            />
          }
          editorProps={{
            quoteId: localQuote.id,
            blocks,
            onBlocksChange: handleBlocksChange,
            variablesMap,
            layoutConfig: localQuote.layoutConfig,
            onActiveBlockChange: setSelectedBlockId,
            selectedBlockId,
          }}
          aiPanel={
            <AIChatPanel
              open={aiPanelOpen}
              onClose={() => setAiPanelOpen(false)}
              quoteId={localQuote.id}
              onCreateBlock={handleAICreateBlock}
              onDeleteBlock={handleAIDeleteBlock}
              onMoveBlock={handleAIMoveBlock}
              onSyncEditor={() => tiptapEditorRef.current?.flushSync()}
            />
          }
        />

        <PublishDialog
          open={publishOpen}
          onOpenChange={(open) => {
            if (!open) setPublishStep(0);
            setPublishOpen(open);
          }}
          step={publishStep}
          loading={publishLoading}
          isPublished={isPublished}
          expiryDate={expiryDate}
          onExpiryDateChange={setExpiryDate}
          publishLocale={publishLocale}
          onPublishLocaleChange={setPublishLocale}
          hasSignaturePage={hasSignaturePage}
          uboSigners={UBO_SIGNERS}
          selectedUboSigner={selectedUboSigner}
          onUboSignerChange={setSelectedUboSigner}
          canSignOnPublish={canSignOnPublish}
          signOnPublish={signOnPublish}
          onSignOnPublishChange={setSignOnPublish}
          senderSignature={senderSignature}
          onSenderSignatureChange={setSenderSignature}
          senderSignedName={senderSignedName}
          contactOptions={contactOptions}
          selectedContactIds={selectedContactIds}
          onContactToggle={handleContactToggle}
          signEmail={signEmail}
          onSignEmailChange={setSignEmail}
          signName={signName}
          onSignNameChange={setSignName}
          shareUrl={shareUrl}
          onCopyLink={handleCopyLink}
          onSendViaHubSpot={canMailViaHubSpot ? () => void handleSendViaHubSpot() : undefined}
          canWithdraw={canWithdraw}
          withdrawLoading={withdrawLoading}
          onWithdraw={() => setWithdrawOpen(true)}
          onConfirm={handleConfirmPublish}
          onClose={() => setPublishOpen(false)}
        />

        <QuoteReviewDialog
          open={reviewOpen}
          state={review.state}
          loading={review.loading}
          requesting={review.requesting}
          assigning={review.assigning}
          error={review.error}
          onOpenChange={setReviewOpen}
          onAssign={(productId, groupId, ownerId) =>
            void review.assignReviewer(productId, groupId, ownerId)
          }
          onRequest={() => void handleRequestReview()}
          onRefresh={() => void review.refresh()}
          onContinueToPublish={() => {
            setReviewOpen(false);
            handleOpenPublish();
          }}
        />

        <WithdrawDialog
          open={withdrawOpen}
          loading={withdrawLoading}
          onOpenChange={(open) => !withdrawLoading && setWithdrawOpen(open)}
          onCancel={() => setWithdrawOpen(false)}
          onConfirm={handleWithdrawQuote}
        />

        <DeletePageDialog
          target={deletePageTarget}
          loading={deletePageLoading}
          onOpenChange={(open) => {
            if (!open && !deletePageLoading) setDeletePageTarget(null);
          }}
          onCancel={() => setDeletePageTarget(null)}
          onConfirm={handleConfirmDeletePage}
        />

        <Dialog
          open={discardTemplateOpen}
          onOpenChange={(open) => {
            if (!templateAction) setDiscardTemplateOpen(open);
          }}
        >
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Wijzigingen verwerpen?</DialogTitle>
              <DialogDescription>
                {isLibraryPage
                  ? 'Alles wat je sinds de laatste keer opslaan aan deze bibliotheekpagina hebt aangepast, gaat verloren. De pagina in de bibliotheek blijft zoals hij was.'
                  : 'Alles wat je sinds de laatste keer opslaan in deze template hebt aangepast, gaat verloren. De template blijft zoals hij was.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDiscardTemplateOpen(false)}
                disabled={Boolean(templateAction)}
              >
                Annuleren
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDiscardTemplateChanges()}
                disabled={Boolean(templateAction)}
              >
                {templateAction === 'discard' ? 'Verwerpen…' : 'Verwerpen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SavePageToLibraryDialog
          page={libraryPage}
          quoteId={localQuote.id}
          onClose={() => setLibraryPage(null)}
          flushChanges={flushEditorAndPendingUpdates}
        />
        <SaveAsTemplateDialog
          open={saveAsTemplateOpen}
          onOpenChange={setSaveAsTemplateOpen}
          name={saveAsTemplateName}
          onNameChange={setSaveAsTemplateName}
          description={saveAsTemplateDesc}
          onDescriptionChange={setSaveAsTemplateDesc}
          loading={saveAsTemplateLoading}
          onCancel={() => setSaveAsTemplateOpen(false)}
          onSave={handleSaveAsTemplate}
        />
      </div>
    </EditorProvider>
  );
}
